import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Copy, Check, ExternalLink, Sparkles, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getFullProfileUrl } from '@/utils/profileUrl';

interface PublicProfileWidgetProps {
  username?: string | null;
}

export default function PublicProfileWidget({ username }: PublicProfileWidgetProps) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const profileUrl = username ? getFullProfileUrl(username) : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  if (!username) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border border-dashed border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50 px-5 py-4">
        <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">
            {t('candidate.publicProfile.setupTitle')}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('candidate.publicProfile.setupDescription')}
          </p>
        </div>
        <button
          onClick={() => navigate('/candidate/profile/edit#sharing')}
          className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {t('candidate.publicProfile.setupCta')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-600 to-indigo-700 px-5 py-4 shadow-sm">
      <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
        <Globe className="w-4 h-4 text-white" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white leading-none">
          {t('candidate.publicProfile.activeTitle')}
        </p>
        <p className="text-xs text-blue-200 mt-0.5">{t('candidate.publicProfile.activeSubtitle')}</p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-medium transition-colors"
        >
          {copied ? (
            <><Check className="w-3.5 h-3.5 text-green-300" /><span className="text-green-300">{t('candidate.publicProfile.copied')}</span></>
          ) : (
            <><Copy className="w-3.5 h-3.5" />{t('candidate.publicProfile.copy')}</>
          )}
        </button>

        <a
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-medium transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {t('candidate.publicProfile.viewPortfolio')}
        </a>

        <button
          onClick={() => navigate('/candidate/profile/edit#sharing')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-medium transition-colors"
        >
          <Settings className="w-3.5 h-3.5" />
          {t('candidate.publicProfile.customize')}
        </button>
      </div>
    </div>
  );
}
